import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy,Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatCard, MatCardActions, MatCardContent, MatCardHeader, MatCardTitle } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatFormField, MatLabel, MatOption, MatSelect } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, RouterLink } from '@angular/router';

import { AdminComponentBase, PageSimpleContentComponent, PageStatus } from 'app/layout/content';
import { ViewContextService } from 'app/navigation/view-context.service';
import { AuthorizationContext, AuthorizationService } from 'app/shared/authentication';
import { ErrorComponent } from 'app/shared/components/error.component';
import { SaveButtonComponent } from 'app/shared/components/save-button.component';
import { ConfirmDialogService } from 'app/shared/dialog/confirm-dialog-service';
import { GlobalPermission, Photo, StorePermission } from 'app/shared/graph';
import { CopyToClipboardComponent } from 'app/shared/utilities/copy-to-clipboard.component';
import { FromNowPipe } from 'app/shared/utilities/from-now.pipe';

import { PhotosService } from './photos.service';

@Component({
    templateUrl: './photo-page.component.html',
    styleUrls: ['./photo-page.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [PageSimpleContentComponent, CopyToClipboardComponent, SaveButtonComponent, ErrorComponent, RouterLink, FromNowPipe, DatePipe, MatButton, MatIcon, MatProgressSpinner, MatSelect, FormsModule, MatOption, MatFormField, MatLabel, MatCard, MatCardContent, MatCardHeader, MatCardActions, MatCardTitle],
})
export class PhotoPageComponent extends AdminComponentBase implements OnInit, OnDestroy {
    private readonly route = inject(ActivatedRoute);
    private readonly authorizationService = inject(AuthorizationService);
    private readonly photosService = inject(PhotosService);
    private readonly confirmDialogService = inject(ConfirmDialogService);
    private readonly matSnackBar = inject(MatSnackBar);
    private readonly viewContextService = inject(ViewContextService);

    versionToReplace = 'original';
    showUploadForm = false;
    replacingImage = false;
    photoUrn: string;
    hubUserId: string;
    authContext: AuthorizationContext;
    photoInfo?: Photo;
    showAutoCrops = false;
    colors = [
        '#ff8100',
        '#37a198',
        '#923bcd'
    ];
    fileSelected: number;

    ngOnInit(): void {
        this.authorizationService.authorizationContext().subscribe((authContext) => {
            this.authContext = authContext;
        });
        this.registerSubscription(
            this.route.params.subscribe(params => {
                this.photoUrn = params.photoUrn;
                this.hubUserId = params.hubUserId;
                this.loadPhoto();
            })
        );
        this.setPageStatus(PageStatus.loaded);
    }

    ngOnDestroy(): void {
        this.unsubscribeSubscriptions();
    }

    loadPhoto() {
        if (this.hubUserId) {
            this.subscribeWithGenericLoadingErrorHandling(this.photosService.getPhotoInfoForUser(this.hubUserId, this.photoUrn), (photoInfo) => {
                this.photoInfo = photoInfo ?? undefined;
                this.pageStatus = PageStatus.loaded;
                this.showAutoCrops = false;
                if (photoInfo)
                    this.viewContextService.selectStore(photoInfo.storeId);
            });
        } else {
            this.subscribeWithGenericLoadingErrorHandling(this.photosService.getPhotoInfo(this.photoUrn), (photoInfo) => {
                this.photoInfo = photoInfo ?? undefined;
                this.pageStatus = PageStatus.loaded;
                this.showAutoCrops = false;
                if (photoInfo)
                    this.viewContextService.selectStore(photoInfo.storeId);
            });
        }
    }

    reEncodeImage(urn: string) {
        this.confirmDialogService
            .confirm('Re-Encode Photo', 'Do you really want to re-encode this photo?',
                'This should only be used if this photo cannot be rendered by the production server', {
                    yes: {
                        warn: true,
                        label: 'Re-Encode'
                    },
                    no: {
                        label: 'Cancel'
                    }
                })
            .subscribe(() => {
                this.subscribeWithGenericSavingErrorHandling(this.photosService.reEncodePhoto(urn), () => {
                    this.loadPhoto();
                });
            });
    }

    invalidateImageCache(_: string) {
        this.confirmDialogService.confirm(
            'Invalidate cache',
            'Do you want to invalidate cache for this photo?',
            'If the user token to the photo source is not valid anymore this project will not be able to be rendered.',
            {
                yes: {
                    warn: true,
                    label: 'Invalidate'
                },
                no: {
                    label: 'Cancel'
                }
            }
        ).subscribe(() => {
                this.subscribeWithGenericSavingErrorHandling(this.photosService.invalidateExternalPhotoCache(this.photoUrn));
            }
        );
    }

    canManagePhoto() {
        if (this.photoInfo?.storeId) {
            return this.authContext.hasStorePermissions(this.photoInfo.storeId, this.photoInfo.store.subscriptionId, StorePermission.ManagePhotos);
        }
        return false;
    }

    canViewApplicationInsights() {
        return this.authContext.hasGlobalPermissions(GlobalPermission.ReadApplicationInsightsLogs);
    }

    private fitInImage(sourceWidth: number, sourceHeight: number, targetWidth: number, targetHeight: number): { width: number; height: number; x: number; y: number } {
        const ratio = Math.min(targetWidth / sourceWidth, targetHeight / sourceHeight);
        const width = sourceWidth * ratio;
        const height = sourceHeight * ratio;
        const x = (targetWidth - width) / 2;
        const y = (targetHeight - height) / 2;
        return { width, height, x, y };
    }

    drawAutoCrops(canva: HTMLCanvasElement, photoInfo: Photo) {
        this.showAutoCrops = true;
        let context = canva.getContext('2d');
        const img = new Image();
        let version = photoInfo.versions[0];
        img.src = version.url;
        img.onload = () => {
            const dimensions = this.fitInImage(version.width, version.height, 500, 500);
            context?.drawImage(img, 0, 0, version.width, version.height, dimensions.x, dimensions.y, dimensions.width, dimensions.height);

            if (context && photoInfo.autoCrops) {

                for (let i = 0; i < photoInfo.autoCrops.length; i++) {
                    const crop = photoInfo.autoCrops[i];
                    context.strokeStyle = this.colors[i];
                    context.lineWidth = 5;

                    const scaledX = (crop.x * dimensions.width / photoInfo.width) + dimensions.x;
                    const scaledY = (crop.y * dimensions.height / photoInfo.height) + dimensions.y;
                    const scaledWidth = (crop.width * dimensions.width / photoInfo.width);
                    const scaledHeight = (crop.height * dimensions.height / photoInfo.height);

                    context.strokeRect(scaledX, scaledY, scaledWidth, scaledHeight);
                }
            }
        };
    }

    displayReplaceOriginalImageForm() {
        this.showUploadForm = true;
    }

    incFileSelected() {
        this.fileSelected++;
    }

    uploadImage(files: FileList | null) {
        if (!files) {
            return;
        }
        const file = files[0];
        this.replacingImage = true;
        this.photosService.replacePhotoImage(this.photoUrn, this.versionToReplace, file).subscribe({
            next: () => {
                this.replacingImage = false;
                this.matSnackBar.open('Image replaced', 'OK', {
                    duration: 2000
                });
                this.loadPhoto();
            },
            error: (err) => {
                this.replacingImage = false;
                this.setSavingError(err);
            }
        });
    }

    async downloadFile($event: Event, url: string) {
        $event.preventDefault();
        $event.stopPropagation();
        $event.stopImmediatePropagation();
        if (!this.photoInfo) {
            return;
        }
        let response = await fetch(url);
        let blob = await response.blob()
        let downloadUrl = new URL(url);
        let pathSplit = downloadUrl.pathname.split('/');
        let filename = pathSplit[pathSplit.length - 1];
        this.downloadBlob(blob, this.photoInfo.urn + '.' + filename);
    }

    downloadBlob(blob: Blob, fileName: string) {
        const downloadLink = document.createElement('a');
        const blobUrl = URL.createObjectURL(blob);
        downloadLink.href = blobUrl;
        downloadLink.download = fileName;
        document.body.appendChild(downloadLink);

        downloadLink.click();

        document.body.removeChild(downloadLink);
        URL.revokeObjectURL(blobUrl);
    }
    openLog(openLogsUrl: string) {
        window.open(openLogsUrl, '_blank');
    }
}
